import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, API_URL } from '@/lib/api';

/**
 * What documents hang off. Repair-request photos have their own API, because their
 * rule is "only the person who reported it" and this one cannot express that.
 *
 * 'handover' and 'return' are two views of the same Assignment row - photographed on
 * the way out and again on the way back. Kept apart on purpose: the whole value is
 * being able to put "the lid when Priya got it" beside "the lid when she gave it
 * back", and one merged pile of photos answers nothing.
 */
export type DocumentEntity = 'asset' | 'exit' | 'handover' | 'return';

export interface AssetDocument {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  uploadedBy?: { name: string } | null;
}

export const documentKeys = {
  all: ['documents'] as const,
  list: (entity: DocumentEntity, entityId: string) =>
    ['documents', entity, entityId] as const,
};

export function useDocuments(entity: DocumentEntity, entityId: string, enabled = true) {
  return useQuery({
    queryKey: documentKeys.list(entity, entityId),
    queryFn: () => api.get<AssetDocument[]>(`/documents/${entity}/${entityId}`),
    enabled: enabled && entityId !== '',
  });
}

export function useUploadDocument(entity: DocumentEntity, entityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<AssetDocument>(`/documents/${entity}/${entityId}`, form);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: documentKeys.list(entity, entityId) }),
  });
}

export function useDeleteDocument(entity: DocumentEntity, entityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) =>
      api.delete<{ id: string }>(`/documents/${attachmentId}`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: documentKeys.list(entity, entityId) }),
  });
}

/**
 * Where a file is served from.
 *
 * A plain URL rather than a fetch-and-blob, so the browser streams it and the session
 * cookie goes with it - the route is authorised the same way every other one is.
 */
export function documentUrl(attachmentId: string): string {
  return `${API_URL}/documents/file/${attachmentId}`;
}
